<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="(!isPhoneNumberValid && isPhoneNumberForCancelType) || isLoading"
    :hide-reset="!!rideInfo"
    loading-message="Canceling Ride..."
    @submit="cancelRide"
    @reset="resetForm"
  >
    <template #title>
      Cancel Ride
    </template>

    <template #content>
      <fieldset>
        <div
          v-if="isOperatorDashboard"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Cancel By* </label>
          <div class="col-sm-7">
            <dropdown-list
              v-model="requestBody.cancelType"
              :disabled="!!rideInfo"
              :options="cancelTypeList"
            />
          </div>
        </div>

        <div
          v-if="!isFamilyDashboard"
          v-show="isPhoneNumberForCancelType"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Phone Number* </label>
          <phone-number-input
            v-model="requestBody.phoneNumber"
            class="col-sm-7"
            :page-id="pageId"
          />
        </div>

        <div
          v-if="isRequestIdForCancelType"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Request ID* </label>
          <div class="col-sm-7">
            <sanitized-input
              id="requestId"
              v-model="requestBody.requestId"
              placeholder="Please input request id"
              required
              :disabled="!!rideInfo"
            />
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Cancel Reason* </label>
          <div class="col-sm-7">
            <dropdown-list
              v-model="requestBody.cancelReason"
              :options="cancelReasonList"
              required
              filterable
            />
          </div>
        </div>

        <div
          v-if="isOtherCancelReason || isFalseTripCancelReason"
          class="row"
        >
          <label class="col-sm-3 control-label pt-5"> {{ isOtherCancelReason ? 'Other Reason' : 'Detail Reason' }}* </label>
          <div class="col-sm-7">
            <sanitized-textarea
              id="otherReason"
              v-model="requestBody.otherReason"
              placeholder="The driver doesn't arrive in time."
              autosize
              required
            />
          </div>
        </div>
      </fieldset>
    </template>

    <template #submit-button>
      Cancel Ride
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { computed, ref, Ref, watch, onMounted } from 'vue';

import util from '@/Utils/util';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { sendDelete } from '@/Utils/api';
import { ListType } from '@/models/app';
import { callerService } from '@/services/caller';

const pageId = 'cancel-ride';

const cancelTypeList: ListType[] = [
  { id: 1, key: 'phoneNumber', label: 'Phone Number' },
  { id: 2, key: 'requestId', label: 'Request ID' },
];
const cancelReasonList: ListType[] = [
  { id: 1, key: 'Car too big', label: 'Car too big' },
  { id: 2, key: 'Car too small', label: 'Car too small' },
  { id: 3, key: 'Driver asked to cancel', label: 'Driver asked to cancel' },
  { id: 4, key: 'ETA too long', label: 'ETA too long' },
  { id: 5, key: 'False trip', label: 'Rider Not in the Car' },
  { id: 6, key: 'No drivers available', label: 'No drivers available' },
  { id: 7, key: 'No longer needs/wants ride', label: 'No longer needs/wants ride' },
  { id: 8, key: 'Ordered accidentally by rider', label: 'Ordered accidentally by rider' },
  { id: 9, key: 'Other', label: 'Other' },
  { id: 10, key: 'Pharmacy/Special Request driver did not agree', label: 'Pharmacy/Special Request driver did not agree' },
  { id: 11, key: 'Preferred other vendor', label: 'Preferred other vendor' },
  { id: 12, key: 'Repairing with previous driver', label: 'Repairing with previous driver' },
  { id: 13, key: 'Rider not ready to go', label: 'Rider not ready to go' },
  { id: 14, key: 'Rider pressed wrong button', label: 'Rider pressed wrong button' },
  { id: 15, key: 'Rider requested different driver', label: 'Rider requested different driver' },
  { id: 16, key: 'Stuck in processing', label: 'Stuck in processing' },
  { id: 17, key: 'Wanted lower rate', label: 'Wanted lower rate' },
  { id: 18, key: 'Wrong drop-off address', label: 'Wrong drop-off address' },
  { id: 19, key: 'Wrong pickup address', label: 'Wrong pickup address' },
];

const props = defineProps({
  rideInfo: {
    type: Object,
    default: () => null,
  },
});

const { isOperatorDashboard, isFamilyDashboard, isPartnerDashboard } = useDashboard();
const { customerPhoneNumber } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { partnerId } = usePartner();

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(requestBody.value.phoneNumber));

const isPhoneNumberForCancelType = computed(() => requestBody.value.cancelType === 'phoneNumber');

const isRequestIdForCancelType = computed(() => requestBody.value.cancelType === 'requestId');

const isFalseTripCancelReason = computed(() => requestBody.value.cancelReason === 'False trip');

const isOtherCancelReason = computed(() => requestBody.value.cancelReason === 'Other');

const requestBody: Ref<any> = ref({
  phoneNumber: '',
  requestId: '',
  otherReason: '',
  cancelType: cancelTypeList[0].key,
  cancelReason: cancelReasonList[0].key,
});

const isLoading = ref(false);

const cancelRide = async () => {
  try {
    const isCancelConfirm = await showConfirmPopupDialog({
      title: 'Are you sure to cancel this ride?',
      confirmButtonText: 'Cancel',
    });

    if (!isCancelConfirm) {
      return;
    }

    isLoading.value = true;

    const opCancellationReason = isOtherCancelReason.value
      ? requestBody.value.otherReason
      : isFalseTripCancelReason.value
        ? `False trip - ${requestBody.value.otherReason}`
        : requestBody.value.cancelReason;

    const data = {
      opCancellationReason,
    } as any;

    if (isRequestIdForCancelType.value) {
      data['requestId'] = requestBody.value.requestId;
    } else {
      data['phoneNumber'] = requestBody.value.phoneNumber;
    }

    // if the page is in Partner dashboard, firstly we should check caller is in partnership
    if (isPartnerDashboard.value && partnerId.value) {
      await callerService.fetchCustomerInfo({
        phoneNumber: requestBody.value.phoneNumber,
        partnerId: partnerId.value,
      });
    }

    // DELETE /rides
    /*
      body => {
        requestId: ***,
        phoneNumber: ***,
        opCancellationReason: ***
      }
    */
    const response = await sendDelete('rides', {
      data,
    });

    showSuccessPopupDialog({
      text: `Ride #${response.data.requestId} has been canceled for ${response.data.callerFullName}(${requestBody.value.phoneNumber}.`,
    });
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const resetForm = () => {
  requestBody.value = {
    phoneNumber: isFamilyDashboard.value ? customerPhoneNumber.value : '',
    cancelType: cancelTypeList[0].key,
    otherReason: '',
    requestId: '',
    cancelReason: cancelReasonList[0].key,
  };
};

watch(customerPhoneNumber, (value: string) => {
  if (props.rideInfo) {
    return;
  }

  resetForm();
  requestBody.value.phoneNumber = value;
});

onMounted(() => {
  if (props.rideInfo) {
    requestBody.value.cancelType = cancelTypeList[1].key;
    requestBody.value.requestId = props.rideInfo.requestId;
  } else {
    requestBody.value.phoneNumber = customerPhoneNumber.value || '';
  }
});
</script>
